import React, { useState, useContext, useCallback } from 'react';
import { FileText, X, Trash2, Copy } from 'lucide-react';
import { DevLogContext, isLocalNetwork } from '@/contexts/DevLogContext';
import { useAdmin } from '@/contexts/AdminContext';

/**
 * Botão flutuante + painel de logs. Visível apenas em rede local (localhost / LAN).
 * Não afeta layout (position: fixed).
 */
export default function DevLogButton() {
  const { logs, clearLogs, isLocal } = useContext(DevLogContext);
  const { isCurrentUserAdmin } = useAdmin();
  const [open, setOpen] = useState(false);

  const formatForCopy = useCallback(() => {
    return logs
      .map((e) => {
        const dataStr = e.data != null ? ` ${JSON.stringify(e.data)}` : '';
        return `[${e.ts}] ${e.level.toUpperCase()}: ${e.message}${dataStr}`;
      })
      .join('\n');
  }, [logs]);

  const handleCopy = useCallback(async () => {
    const text = formatForCopy();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      // Feedback breve - poderia usar toast se quiser
      const btn = document.querySelector('[data-devlog-copy]');
      if (btn) {
        const prev = btn.textContent;
        btn.textContent = 'Copiado!';
        setTimeout(() => { btn.textContent = prev; }, 1500);
      }
    } catch (err) {
      console.warn('Falha ao copiar logs:', err);
    }
  }, [formatForCopy]);

  if (!isCurrentUserAdmin) return null;

  return (
    <>
      {/* Botão flutuante - canto inferior esquerdo para não conflitar com toasts (top-right) */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-4 left-4 z-[9998] w-10 h-10 rounded-full bg-purple-700 hover:bg-purple-600 text-white shadow-lg border border-purple-500/50 flex items-center justify-center transition focus:outline-none focus:ring-2 focus:ring-purple-400"
        title="Logs de desenvolvimento"
        aria-label="Abrir painel de logs"
      >
        <FileText size={20} />
        {logs.length > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-[10px] font-bold text-gray-900 flex items-center justify-center">
            {logs.length > 99 ? '99+' : logs.length}
          </span>
        )}
      </button>

      {/* Painel de logs */}
      {open && (
        <div
          className="fixed left-4 bottom-16 z-[9999] w-[min(400px,calc(100vw-2rem))] max-h-[70vh] flex flex-col rounded-lg border border-purple-500/40 bg-gray-900 shadow-2xl"
          role="dialog"
          aria-label="Painel de logs"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-purple-500/20 bg-gray-800/80 rounded-t-lg">
            <span className="text-sm font-semibold text-white">Logs (dev)</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-1.5 text-gray-400 hover:text-white rounded transition"
              aria-label="Fechar"
            >
              <X size={18} />
            </button>
          </div>
          <div className="flex-1 overflow-auto p-2 font-mono text-xs text-gray-300 space-y-1 min-h-0">
            {logs.length === 0 ? (
              <p className="text-gray-500 py-4 text-center">Nenhum log ainda.</p>
            ) : (
              logs.map((e) => (
                <div
                  key={e.id}
                  className={`rounded px-2 py-1 break-all ${
                    e.level === 'error'
                      ? 'bg-red-900/30 text-red-200'
                      : e.level === 'warn'
                        ? 'bg-amber-900/20 text-amber-200'
                        : 'bg-gray-800/50'
                  }`}
                >
                  <span className="text-gray-500 shrink-0">[{e.ts.slice(11, 23)}]</span>{' '}
                  <span className="uppercase text-[10px] font-semibold text-purple-400">{e.level}</span>{' '}
                  {e.message}
                  {e.data != null && (
                    <pre className="mt-1 text-[10px] text-gray-500 overflow-x-auto whitespace-pre-wrap">
                      {JSON.stringify(e.data)}
                    </pre>
                  )}
                </div>
              ))
            )}
          </div>
          <div className="flex gap-2 px-3 py-2 border-t border-purple-500/20 bg-gray-800/80 rounded-b-lg">
            <button
              type="button"
              onClick={clearLogs}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-gray-700 hover:bg-gray-600 text-gray-200 text-sm transition"
            >
              <Trash2 size={14} />
              Limpar
            </button>
            <button
              type="button"
              onClick={handleCopy}
              data-devlog-copy
              className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-purple-700 hover:bg-purple-600 text-white text-sm transition"
            >
              <Copy size={14} />
              Copiar
            </button>
          </div>
        </div>
      )}
    </>
  );
}
